import { initComponent } from './core/init-component';

initComponent('.js-ls-newsletter-subscription', (el) => {
  const form = el as HTMLFormElement;
  form.setAttribute('novalidate', '');
  const inputEl       = el.querySelector<HTMLInputElement>('.js-ls-input-button__input');
  const inputBtnEl    = el.querySelector<HTMLElement>('.js-ls-input-button');
  const submitBtn     = el.querySelector<HTMLButtonElement>('.js-ls-newsletter-submit-btn');
  const submitFill    = el.querySelector<HTMLElement>('.js-ls-newsletter-submit-btn__fill');
  const submitLabelEl = el.querySelector<HTMLElement>('.js-ls-newsletter-submit-btn__label');

  function isValidEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function triggerFill(): Promise<void> {
    return new Promise(resolve => {
      if (!submitFill) { resolve(); return; }
      const timeout = setTimeout(() => resolve(), 1500);
      submitFill.addEventListener('transitionend', () => {
        clearTimeout(timeout);
        resolve();
      }, { once: true });
      submitFill.style.width = '100%';
    });
  }

  // Play the fill animation, then hand off to the native Shopify submit; final state is server-rendered via form.posted_successfully? on reload
  async function handleSend() {
    const email = inputEl?.value.trim() ?? '';
    if (!email || !isValidEmail(email)) {
      inputBtnEl?.dispatchEvent(new CustomEvent('ls-input-button:show-error'));
      return;
    }

    if (submitBtn) {
      submitBtn.disabled = true;
      // Snap the base to translucent (no fade) so the white fill sweep looks identical on mobile and desktop
      submitBtn.style.transition = 'none';
      submitBtn.style.backgroundColor = 'var(--color-surface-white-15)';
    }

    await triggerFill();
    // Swap the label the moment the fill completes (Figma: text changes on completion), before the native reload
    if (submitLabelEl && submitBtn?.dataset.submittedLabel) {
      submitLabelEl.textContent = submitBtn.dataset.submittedLabel;
    }
    form.submit();
  }

  function syncBtnState() {
    if (!submitBtn) return;
    const email = inputEl?.value.trim() ?? '';
    submitBtn.disabled = email.length > 0 && !isValidEmail(email);
  }

  function onSubmitGuard(e: Event) { e.preventDefault(); }

  function onBtnClick() { handleSend(); }

  function onInputKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') handleSend();
  }

  // Block any unhandled native form submissions (e.g. mobile keyboard "Go" button)
  form.addEventListener('submit', onSubmitGuard);

  // Already subscribed (button disabled server-side): keep the form inert so typing can't re-enable the button
  if (submitBtn?.disabled) {
    // Strip the customer_posted param so a manual reload resets the form and allows a new submit
    const url = new URL(window.location.href);
    if (url.searchParams.has('customer_posted')) {
      url.searchParams.delete('customer_posted');
      history.replaceState({}, '', url.toString());
    }
    return { unload: () => form.removeEventListener('submit', onSubmitGuard) };
  }

  submitBtn?.addEventListener('click', onBtnClick);
  inputEl?.addEventListener('keydown', onInputKeydown);
  inputEl?.addEventListener('input', syncBtnState);
  inputEl?.addEventListener('change', syncBtnState);
  inputEl?.addEventListener('blur', syncBtnState);

  return {
    unload() {
      form.removeEventListener('submit', onSubmitGuard);
      submitBtn?.removeEventListener('click', onBtnClick);
      inputEl?.removeEventListener('keydown', onInputKeydown);
      inputEl?.removeEventListener('input', syncBtnState);
      inputEl?.removeEventListener('change', syncBtnState);
      inputEl?.removeEventListener('blur', syncBtnState);
    },
  };
});
